import './SingleSelectCombobox.scss'
import { useMemo, useState, useEffect, useRef } from 'react'
import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/shadcn/command'
import { cn } from '@/lib/utils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconDownChevronOutline } from '@icons/IconDownChevronOutline'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { TooltipButton } from '@/components/ui/TooltipButton'

type SingleSelectComboboxProps = {
  placeholder: string
  selected: string | null | undefined
  options: string[]
  onOptionSelected: (option: string) => void
  onClear?: () => void
  class?: string
  lastHierFilter?: boolean
}

export function SingleSelectCombobox(props: SingleSelectComboboxProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const parentRef = useRef<HTMLDivElement | null>(null)
  const [renderedOptions, setRenderedOptions] = useState<string[]>([])
  const [mounted, setMounted] = useState(false)

  // Mark component as mounted and initialize
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Reset search query when popover closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    } else if (mounted) {
      // Ensure we set rendered options as soon as dropdown opens
      const options = Array.isArray(props.options) ? props.options : []
      setRenderedOptions(options)
    }
  }, [open, props.options, mounted])

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    // Ensure options is an array, default to empty array if undefined/null
    const options = Array.isArray(props.options) ? props.options : []

    if (!options.length) return []
    if (!searchQuery.trim()) return options

    const lowerQuery = searchQuery.toLowerCase()
    return options.filter((option) => option && option.toLowerCase().includes(lowerQuery))
  }, [props.options, searchQuery])

  // Update rendered options when filtered options change
  useEffect(() => {
    if (mounted) {
      // Immediate update for filtered options
      setRenderedOptions(filteredOptions)
    }
  }, [filteredOptions, mounted])

  // Determine if we should use virtualization (only for larger lists)
  const useVirtualization = renderedOptions.length > 10

  // Setup virtualization for better performance with large option lists
  const rowVirtualizer = useVirtualizer({
    count: renderedOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 28, // Fixed height for each option item
    overscan: 5, // Number of items to render before/after visible area
  })

  // Define the handler for selection
  const handleSelect = (option: string) => {
    props.onOptionSelected(option)
    setOpen(false)
  }

  // Define classes based on the provided class prop
  // const selectClasses = cn('single-select-combobox flex w-full items-start', props.class && props.class)
  const selectClasses = cn('single-select-combobox flex w-80 items-start', props.class && props.class)

  // Handle opening the dropdown
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)

    // If opening the dropdown, immediately set renderedOptions
    if (isOpen && mounted) {
      const options = Array.isArray(props.options) ? props.options : []
      setRenderedOptions(options)
    }
  }

  return (
    <div className={selectClasses}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            size="sm"
            className={cn(
              'popover-trigger flex w-full items-center justify-between rounded-sm text-xs',
              props.selected ? 'font-medium' : 'font-normal text-muted-foreground'
            )}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={props.placeholder}
          >
            <div className="flex-1 truncate text-left">
              {props.selected ? (
                <TooltipButton tooltip={props.selected}>
                  <div className="w-full truncate">
                    <span className="truncate-text">{props.selected}</span>
                  </div>
                </TooltipButton>
              ) : (
                <span className="text-muted-foreground">{props.placeholder}</span>
              )}
            </div>
            {/* Render the chevron icon */}
            <IconDownChevronOutline className="size-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        {open && (
          <PopoverContent className="single-select-content w-full p-0" align="start" sideOffset={4}>
            <Command shouldFilter={false}>
              <div className="flex items-center border-b px-3">
                <CommandInput
                  className="!bg-background"
                  placeholder={`Search ${props.placeholder.toLowerCase()}...`}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  aria-label={`Search ${props.placeholder}`}
                />
              </div>
              <CommandList>
                {/* Custom empty states - don't use CommandEmpty */}
                {!Array.isArray(props.options) || props.options.length === 0 ? (
                  <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                    No options available
                  </div>
                ) : renderedOptions.length === 0 && searchQuery ? (
                  <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                    No matching options
                  </div>
                ) : (
                  <CommandGroup>
                    {/* List container */}
                    <div
                      ref={parentRef}
                      className="max-h-[200px] w-full overflow-auto"
                      style={{
                        height: Math.min(Math.max(renderedOptions.length * 36, 50), 200),
                        width: '100%',
                      }}
                      role="listbox"
                      aria-label={`${props.placeholder} options`}
                    >
                      {renderedOptions.length === 0 ? (
                        <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                          Loading options...
                        </div>
                      ) : useVirtualization ? (
                        // Virtualized list for many options
                        <div
                          style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                          }}
                        >
                          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const option = renderedOptions[virtualRow.index]
                            if (!option) return null

                            const isSelected = props.selected === option

                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    key={virtualRow.key}
                                    data-index={virtualRow.index}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: `${virtualRow.size}px`,
                                      transform: `translateY(${virtualRow.start}px)`,
                                    }}
                                  >
                                    <CommandItem
                                      value={option}
                                      onSelect={() => handleSelect(option)}
                                      className="!h-7 !p-0.5 text-xs"
                                      aria-selected={isSelected}
                                      role="option"
                                    >
                                      <IconCheckMarkOutline
                                        className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                                        aria-hidden="true"
                                      />
                                      <span className="truncate">{option}</span>
                                    </CommandItem>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="text-offwhite bg-foreground text-xs">
                                  <p>{option}</p>
                                </TooltipContent>
                              </Tooltip>
                            )
                          })}
                        </div>
                      ) : (
                        // Simple list for few options
                        <>
                          {renderedOptions.map((option, index) => {
                            const isSelected = props.selected === option

                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>
                                    <CommandItem
                                      key={index}
                                      value={option}
                                      onSelect={() => handleSelect(option)}
                                      className="!h-7 !p-0.5 text-xs"
                                      aria-selected={isSelected}
                                      role="option"
                                    >
                                      <IconCheckMarkOutline
                                        className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')}
                                        aria-hidden="true"
                                      />
                                      <span className="truncate">{option}</span>
                                    </CommandItem>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="text-offwhite bg-foreground text-xs">
                                  <p>{option}</p>
                                </TooltipContent>
                              </Tooltip>
                            )
                          })}
                        </>
                      )}
                    </div>
                  </CommandGroup>
                )}
              </CommandList>
              {props.onClear && (
                <div className="flex justify-end gap-x-2 border-t p-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() => {
                      if (props.onClear) props.onClear()
                    }}
                    aria-label="Clear selection"
                  >
                    <IconCloseOutline className="mr-2 size-3" aria-hidden="true" />
                    Clear
                  </Button>
                </div>
              )}
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  )
}
